import React, { useState } from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useApi } from '../context/useApi';
import { SearchFnReturn, useBaseList } from '../hook/useBaseList';
import { IProducts, ProductsFilters } from '../models/interfaces';
import { AxiosResponse } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { List } from '../shared/list';
import { convertStringsToSort } from '../utils/sort';
import { Close, Done, LeakRemove, ManageSearch, PostAdd, Reply, Share } from '@mui/icons-material';
import { CustomTooltip } from '../shared/Tooltip/customTooltip';
import { useDialog } from '../context/useDialog';
import { QuickSearchClusterDialog } from '../components/QuickSearchClusterDialog';
import { useMessage } from '../context/useMessage';

const ManualMatch = () => {
  const { state } = useLocation();
  const { t } = useTranslation();
  const { defaultApi } = useApi();

  const { createDialog, closeDialog, createSimpleConfirm } = useDialog();

  const { success, error } = useMessage();

  const navigate = useNavigate();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [selectedIdsForDisassociation, setSelectedIdsForDisassociation] = useState<string[]>([]);

  const searchClusterizedFn = async (
    page: number,
    size: number,
    filters: ProductsFilters,
    sort?: string[],
  ): Promise<SearchFnReturn<IProducts>> => {
    let resp: AxiosResponse<any>;
    if (sort && sort.length > 0) {
      resp = await defaultApi.toReviewClusteredPost({
        params: { page: page, size: size, sort },
        data: state.stateFilters,
      });
    } else {
      resp = await defaultApi.toReviewClusteredPost({
        params: { page: page, size: size },
        data: state.stateFilters,
      });
    }

    if (resp.status === 200) {
      return { count: resp.data?.data.totalElements ?? 0, list: resp.data.data?.content ?? [] };
    } else {
      throw Error('Errore');
    }
  };

  const {
    list: clusterizedList,
    page: clusterizedPage,
    pageSize: clusterizedPageSize,
    count: clusterizedCount,
    isLoading: clusterizedLoading,
    isPreviousData: clusterizedPreviousData,
    handlePageChange: clusterizedHandlePageChange,
    handlePageSizeChange: clusterizedHandlePageSizeChange,
    sort: clusterizedSort,
    onSortClick: clusterizedOnSortClick,
    filtersForm: clusterizedfiltersFormformProps,
  } = useBaseList<IProducts, ProductsFilters>({
    searchFn: searchClusterizedFn,
    overrideFilters: state?.stateFilters,
    apiOptions: { cacheTime: 20000 },
    defaultFilters: {},
    filterStoreKey: 'toReviewClusterized',
  });

  const searchFn = async (
    page: number,
    size: number,
    filters: ProductsFilters,
    sort?: string[],
  ): Promise<SearchFnReturn<IProducts>> => {
    let resp: AxiosResponse<any>;
    if (sort && sort.length > 0) {
      resp = await defaultApi.toReviewManualPost({
        params: { page: page, size: size, sort },
        data: state.stateFilters,
      });
    } else {
      resp = await defaultApi.toReviewManualPost({
        params: { page: page, size: size },
        data: state.stateFilters,
      });
    }

    if (resp.status === 200) {
      return { count: resp.data?.data.totalElements ?? 0, list: resp.data.data?.content ?? [] };
    } else {
      throw Error('Errore');
    }
  };

  const {
    list,
    page,
    pageSize,
    count,
    isLoading,
    isPreviousData,
    handlePageChange,
    handlePageSizeChange,
    sort,
    onSortClick,
    filtersForm,
  } = useBaseList<IProducts, ProductsFilters>({
    searchFn,
    overrideFilters: state?.stateFilters,
    apiOptions: { cacheTime: 20000 },
    defaultFilters: {},
    filterStoreKey: 'toReviewManual',
  });

  const associate = (clusterId?: string, isNewCluster: boolean = false, isAssociation = true) => {
    let cluster_to_associate = state.stateFilters.cluster_id;

    if (isNewCluster) {
      cluster_to_associate = null;
    } else if (clusterId) {
      cluster_to_associate = clusterId;
    }

    const body = {
      is_to_associate: isAssociation,
      cluster_to_associate,
      product_keys: isAssociation ? selectedIds : selectedIdsForDisassociation,
    };

    defaultApi
      .operationsMatchPost({ data: body })
      .then(() => {
        success({ message: t(isAssociation ? 'success.productAssociated' : 'success.productDissociated') });

        setTimeout(() => {
          filtersForm.submit();
          clusterizedfiltersFormformProps.submit();
          setSelectedIds([]);
          setSelectedIdsForDisassociation([]);
        }, 1000);
      })
      .catch(() => {
        error({ message: t(isAssociation ? 'error.productAssociated' : 'error.productDissociated') });
        setSelectedIds([]);
        setSelectedIdsForDisassociation([]);
      });
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={t('actions.goBack')}
        PopperProps={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -10], // sposti tooltip più in alto
              },
            },
          ],
        }}
        slotProps={{
          tooltip: {
            sx: {
              fontSize: '0.85rem', // testo più grande
              padding: '4px 8px', // più spazio interno
            },
          },
        }}
      >
        <IconButton
          size="large"
          sx={{ display: 'inline-flex', padding: 1 }}
          onClick={() => {
            navigate(
              '/clusters',
              state?.stateFilters?.cluster_id
                ? { state: { stateFilters: { cluster_id: state?.stateFilters?.cluster_id } } }
                : {},
            );
          }}
        >
          <Reply />
        </IconButton>
      </Tooltip>
      <Stack
        direction="row"
        ml={'20px'}
        alignItems={'center'}
        justifyContent="space-between"
        spacing={1}
      >
        <Stack direction={'row'} alignItems={'center'} spacing={4} height={'56px'}>
          <Typography variant="h4">{t('manualMatch.title')}</Typography>
        </Stack>
      </Stack>
      <Typography variant="subtitle2" fontSize={'20px'} ml={'20px'} sx={{ my: '10px' }}>
        {t('cluster.id')}: {state.stateFilters.cluster_id}
      </Typography>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant="subtitle1" ml={'20px'}>
          {t('manualMatch.toAssign')}
        </Typography>
        <Stack direction={'row'} spacing={3}>
          <CustomTooltip offset={[0, -10]} title={t('actions.associate')}>
            <IconButton
              size="large"
              disabled={!selectedIds || selectedIds.length < 1}
              onClick={() => {
                createSimpleConfirm({
                  title: t('info.warning'),
                  message: t('info.sureToAssociateProduct'),
                  onConfirm: () => associate(),
                });
              }}
            >
              <Share />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip offset={[0, -10]} title={t('actions.searchCluster')}>
            <IconButton
              size="large"
              disabled={!selectedIds || selectedIds.length < 1}
              onClick={() => {
                createDialog({
                  open: true,
                  maxWidth: false,
                  children: (
                    <QuickSearchClusterDialog
                      onClose={closeDialog}
                      onAssociate={(clusterId) => associate(clusterId)}
                    />
                  ),
                });
              }}
            >
              <ManageSearch />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip offset={[0, -10]} title={t('actions.createCluster')}>
            <IconButton
              size="large"
              disabled={!selectedIds || selectedIds.length < 1}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                createSimpleConfirm({
                  title: t('info.warning'),
                  message: t('info.sureToCreateCluster'),
                  onConfirm: () => associate(undefined, true),
                });
              }}
            >
              <PostAdd />
            </IconButton>
          </CustomTooltip>
        </Stack>
      </Stack>
      <List
        maxheight="calc(100vh - 20px)"
        list={list}
        count={count}
        page={page}
        pageSize={pageSize}
        loading={isLoading}
        isPreviousData={isPreviousData}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        gridTemplateColumns={'1fr 1fr 2fr 2fr 1fr 0.7fr 0.5fr 0.5fr'}
        keyGetter={(product: IProducts) => product.id || ''}
        sortBy={convertStringsToSort(sort)}
        onSortClick={onSortClick}
        onItemClick={(product: IProducts) => {
          if (!product.id) return;
          const id = product.id;
          setSelectedIds((prev) => {
            if (!prev) return [id];
            if (prev.includes(id)) {
              return prev.filter((x) => x !== id);
            } else {
              return [...prev, id];
            }
          });
        }}
        // keepSelectedIds
        onSelectedIdsChange={(e) => {
          setSelectedIds(e);
        }}
        selectedIds={selectedIds}
        fields={[
          {
            title: t('product.managementId'),
            key: 'id_gestionale',
            sortable: false,
          },
          {
            title: t('product.structureId'),
            key: 'id_struttura',
            sortable: false,
          },
          {
            title: t('product.supplierName'),
            key: 'nome_fornitore',
            sortable: false,
          },

          {
            title: t('product.productName'),
            key: 'nome_prodotto',
            sortable: false,
            tooltip: true,
          },
          {
            title: t('product.manufacturerRef'),
            key: 'ref_produttore',
            sortable: false,
            tooltip: true,
          },
          {
            title: t('product.supplierCode'),
            key: 'codice_fornitore',
            sortable: false,
          },
          {
            title: t('product.rdm'),
            key: 'rdm',
            sortable: false,
          },
          {
            title: t('product.confidence'),
            key: 'confidence',
            cellRenderer: (e: IProducts) => {
              return e.confidence ? `${(e.confidence * 100).toFixed(2)} %` : '';
            },
          },
        ]}
        emptyListMessage={t('info.noResult')}
        keepRecentlyOpenedItem
        // lastOpenedItem={lastOpenedItem}
        // actionsHeader={t('actions.title')}
        // actions={
        //   <Stack>
        //     <Button
        //       onClick={() => {
        //         createSimpleConfirm({
        //           title: t('info.warning'),
        //           message: t('info.sureToAssociateAll'),
        //           onConfirm: () => associateAll(),
        //         });
        //       }}
        //       variant="contained"
        //     >
        //       {t('actions.associateAll')}
        //     </Button>
        //   </Stack>
        // }
      />

      <Stack margin={1}></Stack>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography variant="subtitle1" ml={'20px'}>
          {t('manualMatch.assigned')}
        </Typography>
        <Stack direction={'row'} spacing={3}>
          <CustomTooltip offset={[0, -10]} title={t('actions.disassociate')}>
            <IconButton
              size="large"
              disabled={
                clusterizedCount === 1 ||
                !selectedIdsForDisassociation ||
                selectedIdsForDisassociation.length < 1 ||
                (selectedIdsForDisassociation.length === clusterizedCount)
              }
              onClick={() => {
                createSimpleConfirm({
                  title: t('info.warning'),
                  message: t('info.sureToDisassociateProduct'),
                  onConfirm: () => associate(state?.stateFilters?.cluster_id, false, false),
                });
              }}
            >
              <LeakRemove />
            </IconButton>
          </CustomTooltip>
        </Stack>
      </Stack>

      <List
        maxheight="calc(100vh - 20px)"
        list={clusterizedList}
        count={clusterizedCount}
        page={clusterizedPage}
        pageSize={clusterizedPageSize}
        loading={clusterizedLoading}
        isPreviousData={clusterizedPreviousData}
        onPageChange={clusterizedHandlePageChange}
        onPageSizeChange={clusterizedHandlePageSizeChange}
        gridTemplateColumns={'1fr 1fr 2fr 2fr 1fr 0.7fr 0.5fr 0.7fr'}
        keyGetter={(e: any) => e.id as string}
        sortBy={convertStringsToSort(clusterizedSort)}
        onSortClick={clusterizedOnSortClick}
        onItemClick={(product: IProducts) => {
          if (!product.id) return;
          const id = product.id;
          setSelectedIdsForDisassociation((prev) => {
            if (!prev) return [id];
            if (prev.includes(id)) {
              return prev.filter((x) => x !== id);
            } else {
              return [...prev, id];
            }
          });
        }}
        onSelectedIdsChange={(e) => {
          setSelectedIdsForDisassociation(e);
        }}
        selectedIds={selectedIdsForDisassociation}
        fields={[
          {
            title: t('product.managementId'),
            key: 'id_gestionale',
            sortable: false,
          },
          {
            title: t('product.structureId'),
            key: 'id_struttura',
            sortable: false,
          },
          {
            title: t('product.supplierName'),
            key: 'nome_fornitore',
            sortable: false,
          },

          {
            title: t('product.productName'),
            key: 'nome_prodotto',
            sortable: false,
            tooltip: true,
          },
          {
            title: t('product.manufacturerRef'),
            key: 'ref_produttore',
            sortable: false,
            tooltip: true,
          },
          {
            title: t('product.supplierCode'),
            key: 'codice_fornitore',
            sortable: false,
          },
          {
            title: t('product.rdm'),
            key: 'rdm',
            sortable: false,
          },
          {
            title: t('product.manuallyMatched'),
            key: 'manual_matched',
            sortable: false,
            cellRenderer: (e: IProducts) => {
              return (
                <IconButton color="primary">{e.manual_matched ? <Done /> : <Close />}</IconButton>
              );
            },
          },
        ]}
        emptyListMessage={t('info.noResult')}
        keepRecentlyOpenedItem
        // lastOpenedItem={lastOpenedItem}
      />
    </>
  );
};

export default ManualMatch;
